"use client";

import { appConfig } from "@/constants";
import { usePassportProvider } from "@/context";
import { useErc20 } from "@/context/Erc20Provider";
import { useAccessToken } from "@/hooks/useAccessToken";
import type { ChainAddress } from "@/types";
import type { Chain } from "@/types";
import { EnvironmentNames } from "@/types";
import { authorizedGet } from "@/utils/request";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Address } from "viem";
import { createPublicClient, custom } from "viem";
import { immutableZkEvm, immutableZkEvmTestnet } from "viem/chains";
import { getMainnetBalances } from "./mainnet-eject/getMainnetBalances";
import type { MainnetBalance, MinimalCoinBalance } from "./types";
import { addZeroBalanceCoins } from "./utils";

// Minimal ERC20 ABI for balanceOf - only what we need for multicall
const ERC20_BALANCE_OF_ABI = [
  {
    inputs: [
      {
        internalType: "address",
        name: "account",
        type: "address",
      },
    ],
    name: "balanceOf",
    outputs: [
      {
        internalType: "uint256",
        name: "",
        type: "uint256",
      },
    ],
    stateMutability: "view",
    type: "function",
  },
] as const;

/**
 * Generic hook for fetching chain balances.
 * Supports zkEVM, Immutable X, and Ethereum chains.
 */
export function useChainBalances(
  chain: Chain,
  walletAddress: ChainAddress | undefined,
): {
  data: MinimalCoinBalance[] | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: any;
  isLoading: boolean;
  // For Ethereum chain, also expose raw MainnetBalance[] and refetch
  rawData?: MainnetBalance[];
  refetch?: () => void;
} {
  // zkEVM balances via Passport profile API
  const zkEvmQuery = useZkEvmBalances(walletAddress);

  // Immutable X balances via Passport profile API
  const imtblXQuery = useImtblXBalances(walletAddress);

  // Ethereum balances
  const ethQuery = useEthereumBalances(walletAddress, chain === "eth");

  // Return the appropriate query based on chain
  if (chain === "imtbl-zkevm") {
    return zkEvmQuery;
  }
  if (chain === "imtbl-x") {
    return imtblXQuery;
  }
  if (chain === "eth") {
    // Ethereum balances need to be transformed from MainnetBalance to MinimalCoinBalance
    return {
      data: ethQuery.data
        ? ethQuery.data.map((balance) => ({
            name: balance.tokenName,
            symbol: balance.symbol,
            contractAddress:
              balance.address === "ETH"
                ? "native"
                : (balance.address as `0x${string}`),
            balance: balance.balance,
            imageUrl: balance.tokenImage,
            decimals: balance.decimals,
          }))
        : undefined,
      error: ethQuery.error,
      isLoading: ethQuery.isLoading,
      rawData: ethQuery.data,
      refetch: () => {
        void ethQuery.refetch();
      },
    };
  }

  return {
    data: undefined,
    error: null,
    isLoading: false,
  };
}

/**
 * Internal hook for zkEVM balances.
 * Uses viem multicall to batch query token balances directly from the chain.
 * The Passport profile API endpoint `/passport-profile/v1/networks/zkEvm/coins`
 * returns "unsupported network" error, so we query on-chain instead.
 * Now uses ERC20Provider for token metadata when available.
 */
function useZkEvmBalances(walletAddress: ChainAddress | undefined): {
  data: MinimalCoinBalance[] | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: any;
  isLoading: boolean;
} {
  const { zkEvmProvider } = usePassportProvider();
  const { tokens: erc20TokenMap } = useErc20();

  // Determine chainId from environment
  const chainId = useMemo(() => {
    if (appConfig.ENVIRONMENT === EnvironmentNames.PRODUCTION) {
      return immutableZkEvm.id; // 13371
    }
    return immutableZkEvmTestnet.id; // 13473 (testnet/devnet)
  }, []);

  return useQuery<MinimalCoinBalance[]>({
    queryKey: ["chain-balances", "imtbl-zkevm", walletAddress, chainId],
    enabled: !!walletAddress && !!zkEvmProvider,
    queryFn: async () => {
      if (!walletAddress || !zkEvmProvider) return [];

      try {
        const chain =
          chainId === immutableZkEvm.id
            ? immutableZkEvm
            : immutableZkEvmTestnet;

        const publicClient = createPublicClient({
          chain,
          transport: custom(zkEvmProvider),
        });

        // Get native IMX balance
        const nativeBalance = await publicClient.getBalance({
          address: walletAddress as Address,
        });

        const balances: MinimalCoinBalance[] = [];

        // Add native IMX balance if > 0
        if (nativeBalance > 0n) {
          balances.push({
            symbol: chainId === immutableZkEvm.id ? "IMX" : "tIMX",
            contractAddress: "native",
            decimals: 18,
            balance: nativeBalance.toString(),
            name: chainId === immutableZkEvm.id ? "IMX" : "tIMX",
            imageUrl: `${appConfig.TOKEN_ICONS_URL}/imx.svg`,
          });
        }

        // Filter valid tokens and build balance calls
        const validTokens = Array.from(erc20TokenMap.values()).filter(
          (
            token,
          ): token is typeof token & {
            contract_address: string;
            symbol: string;
            decimals: number;
          } =>
            Boolean(
              token.contract_address &&
                token.symbol &&
                token.decimals !== undefined,
            ),
        );

        // Batch query ERC20 token balances using multicall
        if (validTokens.length > 0) {
          const balanceCalls = validTokens.map((token) => ({
            address: token.contract_address.toLowerCase() as Address,
            abi: ERC20_BALANCE_OF_ABI,
            functionName: "balanceOf" as const,
            args: [walletAddress as Address],
          }));

          const results = await publicClient.multicall({
            contracts: balanceCalls,
          });

          // Process results and add non-zero balances
          results.forEach((result, index) => {
            if (
              result.status === "success" &&
              result.result &&
              typeof result.result === "bigint" &&
              result.result > 0n
            ) {
              const token = validTokens[index];
              const addressLower = token.contract_address.toLowerCase();
              const imageUrl =
                token.image_url ||
                `${appConfig.TOKEN_ICONS_URL}/${addressLower}.svg`;

              balances.push({
                symbol: token.symbol,
                contractAddress: addressLower as Address,
                decimals: token.decimals,
                balance: result.result.toString(),
                name: token.name || token.symbol,
                imageUrl,
              });
            }
          });
        }

        // Add zero balance coins (e.g., IMX if balance is 0)
        return addZeroBalanceCoins(chainId, balances);
      } catch (error) {
        console.error("Failed to fetch zkEVM balances:", error);
        // Return zero balance coins on error so UI still shows IMX
        return addZeroBalanceCoins(chainId, []);
      }
    },
  });
}

/**
 * Internal hook for Immutable X balances using Passport profile API
 */
function useImtblXBalances(walletAddress: ChainAddress | undefined): {
  data: MinimalCoinBalance[] | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: any;
  isLoading: boolean;
} {
  const getAccessToken = useAccessToken();

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const query = useQuery<any, any, MinimalCoinBalance[], any>({
    queryKey: ["chain-balances", "imtbl-x", walletAddress],
    enabled: !!walletAddress,
    queryFn: async () => {
      const accessToken = await getAccessToken();
      if (!accessToken || !walletAddress) return [];
      try {
        const data = await authorizedGet(
          "/passport-profile/v1/networks/starkEx/coins",
          accessToken,
        );
        // Ensure we return an array
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Failed to fetch Immutable X balances:", error);
        return [];
      }
    },
    select: (data) => {
      // Handle case where data might not be an array
      if (!data || !Array.isArray(data)) {
        return [];
      }
      return data.map(
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        (coinBalance: any) =>
          <MinimalCoinBalance>{
            name: coinBalance.name,
            symbol: coinBalance.symbol,
            contractAddress: coinBalance.contract_address,
            balance: coinBalance.balance,
            imageUrl: coinBalance.image_url,
            decimals: coinBalance.decimals,
          },
      );
    },
  });

  return query;
}

/**
 * Internal hook for Ethereum balances using Alchemy
 */
function useEthereumBalances(
  walletAddress: ChainAddress | undefined,
  enabled: boolean,
) {
  return useQuery<MainnetBalance[]>({
    queryKey: ["chain-balances", "eth", walletAddress],
    queryFn: async () => {
      if (!walletAddress) {
        return [];
      }
      return getMainnetBalances(walletAddress);
    },
    enabled: enabled && !!walletAddress,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });
}
